import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Sparkles, X } from 'lucide-react';
import { dismissActiveSurveyBanner, mySurveyProgress, type SurveyProgress } from '@/lib/pulse/surveyApi';

interface Props {
  orgId: string;
}

/**
 * High-signal banner that appears on /app/employee when the org has a live
 * survey the viewer has not finished and has not dismissed. Complements the
 * ActiveSurveyCard (which is the "here's your progress" widget).
 */
export function ActiveSurveyBanner({ orgId }: Props) {
  const qc = useQueryClient();
  const queryKey = ['my_survey_progress', orgId];

  const { data } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await mySurveyProgress(orgId);
      if (error) return { active: false as const };
      return (data ?? { active: false as const }) as SurveyProgress;
    },
    enabled: !!orgId,
  });

  const dismiss = useMutation({
    mutationFn: () => dismissActiveSurveyBanner(orgId),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<SurveyProgress>(queryKey);
      if (previous && previous.active) {
        qc.setQueryData(queryKey, { ...previous, banner_dismissed: true });
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(queryKey, ctx.previous);
    },
  });

  if (!data || data.active !== true) return null;
  if (data.banner_dismissed) return null;
  if (data.answered >= data.total_anchors && data.total_anchors > 0) return null;

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/10 text-foreground px-4 py-3 flex items-center gap-3">
      <Sparkles className="h-4 w-4 text-primary shrink-0" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          Your team has a new pulse survey live — {data.survey_topic}
        </p>
        <p className="text-xs text-muted-foreground">
          {data.answered} of {data.total_anchors} answered
        </p>
      </div>
      <Button asChild size="sm" className="shrink-0">
        <Link to="/app/employee/survey">
          {data.answered === 0 ? 'Take it now' : 'Continue'}
        </Link>
      </Button>
      <button
        type="button"
        onClick={() => dismiss.mutate()}
        aria-label="Dismiss"
        className="shrink-0 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
